import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { message } from "utils/ciq-message";
import FeatureLinkingDialog from "components/feature-linking-dialog";
import FeatureItem from "components/feature-linking-dialog/feature-item";
import FeatureLinksDialogHeader from "components/feature-linking-dialog/feature-links-dialog-header";
import FeaturesGrid from "components/feature-linking-dialog/features-grid";
import BidPackageCiqIcon from "components/svg-icons/bid-package-ciq-icon";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import useFeatureToFeatureLinks from "entity-app/hooks/feature-to-feature-links";
import { useCallback, useContext, useMemo, useState } from "react";
import { sortAlphanumeric } from "utils/utils";
import {
  LINK_CARD_HORIZONTAL_MARGIN_PX,
  LINKS_COLUMN_OUTER_PADDING_PX,
  LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
  LINKS_COLUMN_SUCCESSOR_WIDTH,
  LINKS_GROUP_HEADER_VERTICAL_PADDING_PX,
  LINKS_GROUP_TITLE_LEFT_PADDING_PX
} from "../../layout-constants";
import { SuccessorAxisLabelRow } from "../links-column/links-column-axis";
import "./design-package-successors-column.scss";

const DESIGN_PACKAGE_FOCUSED_SUCCESSOR_LAYOUT = {
  designPackageFocusedLayout: true
} as const;

const LINKS_GROUP_CONTAINER_BG = "#0000000A";
const LINKS_GROUP_ACCORDION_ICON_COLOR = "#4448C1";
const LINKS_GROUP_ACCORDION_ICON_SIZE_PX = 14;
const BID_PACKAGES_GROUP_ID = FeatureTypes.BID_PACKAGE;

type TMilestone = {
  id: string;
  name: string;
};

type TLinkedBidPackage = {
  id: string;
  number?: string;
  title?: string;
  source_feature_milestone?: TMilestone;
  source_instance_milestone_id?: string;
};

const linksGroupAccordionIconStyle: React.CSSProperties = {
  fontSize: LINKS_GROUP_ACCORDION_ICON_SIZE_PX,
  width: LINKS_GROUP_ACCORDION_ICON_SIZE_PX,
  height: LINKS_GROUP_ACCORDION_ICON_SIZE_PX,
  color: LINKS_GROUP_ACCORDION_ICON_COLOR
};

const linksGroupCardInsetStyle: React.CSSProperties = {
  paddingLeft: LINK_CARD_HORIZONTAL_MARGIN_PX,
  paddingRight: LINK_CARD_HORIZONTAL_MARGIN_PX
};

function buildFeatureLinkChanges(
  selectedFeatures: TLinkedBidPackage[],
  linkedFeatures: TLinkedBidPackage[] | undefined
) {
  const insertedLinks: TLinkedBidPackage[] = [];
  const updatedLinks: TLinkedBidPackage[] = [];
  const deletedLinks: TLinkedBidPackage[] = [];
  const linkedFeaturesMap = new Map(
    linkedFeatures?.map((feature) => [feature.id, feature])
  );

  selectedFeatures.forEach((feature) => {
    const existingFeature = linkedFeaturesMap.get(feature.id);
    if (existingFeature) {
      if (
        existingFeature.source_feature_milestone?.id !==
        feature.source_feature_milestone?.id
      ) {
        insertedLinks.push(feature);
      }
      linkedFeaturesMap.delete(feature.id);
    } else {
      insertedLinks.push(feature);
    }
  });

  linkedFeaturesMap.forEach((feature) => {
    deletedLinks.push(feature);
  });

  return { insertedLinks, updatedLinks, deletedLinks };
}

function getDefaultMilestone(milestones: TMilestone[]) {
  return milestones[milestones.length - 1];
}

function getLinkTypeForMilestone(
  milestoneId: string | undefined,
  milestones: TMilestone[]
) {
  const lastMilestoneId = getDefaultMilestone(milestones)?.id;
  return milestoneId === lastMilestoneId ? 0 : 4;
}

function generateFeatureLinkPayload(
  featureId: string | undefined,
  changes: ReturnType<typeof buildFeatureLinkChanges>,
  milestones: TMilestone[]
) {
  if (!featureId) return null;

  const { insertedLinks, updatedLinks, deletedLinks } = changes;
  const toLinkMutation = (feature: TLinkedBidPackage) => {
    const sourceInstanceMilestoneId = feature.source_feature_milestone?.id;
    return {
      source_instance_id: featureId,
      source_instance_milestone_id: sourceInstanceMilestoneId,
      target_instance_id: feature.id,
      link_type: getLinkTypeForMilestone(sourceInstanceMilestoneId, milestones),
      coupling_type: 2
    };
  };

  return {
    input: {
      deletes: deletedLinks.map((feature) => ({
        source_instance_id: featureId,
        target_instance_id: feature.id
      })),
      inserts: insertedLinks.map(toLinkMutation),
      updates: updatedLinks.map(toLinkMutation)
    },
    metadata: {
      request_source: "DETAIL_PAGE"
    }
  };
}

function getFeatureMilestone(
  feature: TLinkedBidPackage,
  milestones: TMilestone[]
) {
  return (
    feature.source_feature_milestone ??
    milestones.find(
      (milestone) => milestone.id === feature.source_instance_milestone_id
    ) ??
    getDefaultMilestone(milestones)
  );
}

function withDefaultMilestone(
  feature: TLinkedBidPackage,
  milestones: TMilestone[]
) {
  return {
    ...feature,
    source_feature_milestone: getFeatureMilestone(feature, milestones)
  };
}

function BidPackagesGroupCountBadge(props: Readonly<{ count: number }>) {
  if (props.count <= 0) return null;
  return (
    <span
      className="inline-flex min-w-[18px] shrink-0 items-center justify-center rounded-full border border-solid border-ciq-gray-300 bg-white px-1.5 py-0 text-[10px] font-medium leading-4 text-ciq-gray-700"
      data-testid="links-column-group-count"
    >
      {props.count}
    </span>
  );
}

function DesignSuccessorsLinkAction(
  props: Readonly<{
    children: React.ReactNode;
    onClick: () => void;
    testId: string;
  }>
) {
  return (
    <button
      type="button"
      className="design-successors-link-action"
      onClick={props.onClick}
      data-testid={props.testId}
    >
      {props.children}
    </button>
  );
}

function DesignPackageSuccessorsColumn(
  props: Readonly<{
    featureId: string;
    linkedCount: number;
    linkCards: React.ReactNode;
    disabled?: boolean;
    afterChange?: () => void;
    style?: React.CSSProperties;
    "data-testid"?: string;
    milestones: TMilestone[];
  }>
) {
  const {
    featureId,
    linkedCount,
    linkCards,
    disabled = false,
    afterChange,
    style,
    milestones
  } = props;
  const testId =
    props["data-testid"] ?? "entity-dateblock-new-interface-successors";

  const { tokenRetrievalState } = useContext(ProjectContext);
  const token = tokenRetrievalState?.token ?? "";

  const [isExpanded, setIsExpanded] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inEditMode, setInEditMode] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<TLinkedBidPackage[]>(
    []
  );

  const {
    linkedFeatures,
    featuresList,
    saveLinks,
    isLoadingLinkedFeatures,
    isLoadingFeaturesList,
    isSavingLinks,
    linkedFeaturesError,
    resetFeaturesListStates
  } = useFeatureToFeatureLinks({
    token,
    featureInstanceId: featureId,
    featureTypeId: FeatureTypes.BID_PACKAGE,
    linkType: "child",
    loadLinkedFeatures: true,
    loadFeaturesList: isModalOpen
  });

  const openLinkingDialog = () => {
    setSelectedFeatures(
      (linkedFeatures ?? []).map((feature: TLinkedBidPackage) =>
        withDefaultMilestone(feature, milestones)
      )
    );
    setInEditMode(false);
    setIsModalOpen(true);
  };

  const onCheckBoxAction = (data: {
    checked: boolean;
    feature: TLinkedBidPackage;
  }) => {
    setInEditMode(true);
    if (data.checked) {
      setSelectedFeatures((prev) => [
        withDefaultMilestone(data.feature, milestones),
        ...prev
      ]);
    } else {
      setSelectedFeatures((prev) =>
        prev.filter((feature) => feature.id !== data.feature.id)
      );
    }
  };

  const onUnlink = useCallback((feature: TLinkedBidPackage) => {
    setInEditMode(true);
    setSelectedFeatures((prev) => prev.filter((f) => f.id !== feature.id));
  }, []);

  const onMilestoneChange = useCallback(
    (selectedFeatureId: string, milestone: TMilestone) => {
      setInEditMode(true);
      setSelectedFeatures((prev) =>
        prev.map((feature) =>
          feature.id === selectedFeatureId
            ? { ...feature, source_feature_milestone: milestone }
            : feature
        )
      );
    },
    []
  );

  const onSave = async () => {
    const linkedBidPackages = (linkedFeatures ?? []).map(
      (feature: TLinkedBidPackage) => withDefaultMilestone(feature, milestones)
    );
    const changes = buildFeatureLinkChanges(
      selectedFeatures,
      linkedBidPackages
    );
    const payload = generateFeatureLinkPayload(featureId, changes, milestones);
    const response = await saveLinks(payload);

    if (response.success) {
      setIsModalOpen(false);
      resetFeaturesListStates();
      setInEditMode(false);
      message.success("Bid Package links updated successfully");
      afterChange?.();
    }
  };

  const linkedFeaturesDOMForDialog = useMemo(() => {
    // `selectedFeatures` is state seeded with `[]` and only ever set to arrays,
    // so `!selectedFeatures` is never true and the loading operand is dead.
    /* v8 ignore next */
    if ((!selectedFeatures && isLoadingLinkedFeatures) || linkedFeaturesError) {
      return null;
    }

    if (selectedFeatures.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center text-xs italic text-ciq-gray-950 opacity-80 pt-14">
          No Bid Packages linked
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {sortAlphanumeric(selectedFeatures, "number").map((link) => (
          <FeatureItem
            key={link.id}
            number={link.number ?? ""}
            title={link.title ?? ""}
            onUnlink={() => onUnlink(link)}
            milestones={milestones}
            selectedFeatureMilestone={link.source_feature_milestone}
            showMilestoneSelect
            onMilestoneChange={(milestone) =>
              onMilestoneChange(link.id, milestone)
            }
          />
        ))}
      </div>
    );
  }, [
    isLoadingLinkedFeatures,
    linkedFeaturesError,
    onUnlink,
    onMilestoneChange,
    selectedFeatures,
    milestones
  ]);

  const editLinksAction =
    !disabled && linkedCount > 0 ? (
      <DesignSuccessorsLinkAction
        onClick={openLinkingDialog}
        testId="design-bid-packages-edit-links"
      >
        Edit Links
      </DesignSuccessorsLinkAction>
    ) : null;

  return (
    <>
      <div
        className="shrink-0 flex flex-col pt-8 self-start"
        style={{ width: LINKS_COLUMN_SUCCESSOR_WIDTH, ...style }}
        data-testid={testId}
      >
        <div className="h-10 shrink-0" />
        <SuccessorAxisLabelRow
          title="Successors"
          count={linkedCount > 0 ? linkedCount : undefined}
          focusedLayout={DESIGN_PACKAGE_FOCUSED_SUCCESSOR_LAYOUT}
        />
        <div
          className="min-w-0 py-2 space-y-2"
          style={{
            paddingLeft: LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
            paddingRight: LINKS_COLUMN_OUTER_PADDING_PX
          }}
        >
          <div
            className="min-w-0 overflow-hidden rounded-lg"
            style={{ backgroundColor: LINKS_GROUP_CONTAINER_BG }}
            data-testid={`links-column-group-${BID_PACKAGES_GROUP_ID}`}
          >
            <div
              className="flex w-full items-center justify-between select-none"
              style={{
                paddingLeft: LINKS_GROUP_TITLE_LEFT_PADDING_PX,
                paddingRight: LINK_CARD_HORIZONTAL_MARGIN_PX,
                paddingTop: LINKS_GROUP_HEADER_VERTICAL_PADDING_PX,
                paddingBottom: LINKS_GROUP_HEADER_VERTICAL_PADDING_PX
              }}
              data-testid="links-column-group-header"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div className="rounded-sm flex items-center justify-center shrink-0 w-5 h-5 bg-ciq-red-200">
                  <BidPackageCiqIcon
                    size={10}
                    opacity="1"
                    className="fill-ciq-red-700"
                  />
                </div>
                <span
                  className="truncate text-xs font-semibold text-ciq-gray-800"
                  data-testid="links-column-group-label"
                >
                  Bid Packages
                </span>
                <BidPackagesGroupCountBadge count={linkedCount} />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {editLinksAction}
                <button
                  type="button"
                  className="border-0 bg-transparent cursor-pointer p-0 leading-none"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  aria-expanded={isExpanded}
                  data-testid="links-column-group-accordion"
                >
                  {isExpanded ? (
                    <UpOutlined style={linksGroupAccordionIconStyle} />
                  ) : (
                    <DownOutlined style={linksGroupAccordionIconStyle} />
                  )}
                </button>
              </div>
            </div>
            {isExpanded ? (
              <div
                className="min-w-0 space-y-2"
                style={{
                  ...linksGroupCardInsetStyle,
                  paddingBottom: LINK_CARD_HORIZONTAL_MARGIN_PX
                }}
                data-testid="links-column-group-content"
              >
                {linkedCount > 0 ? (
                  <div
                    className="space-y-2"
                    data-testid="design-bid-packages-list"
                  >
                    {linkCards}
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between rounded border border-solid border-ciq-black-a10 bg-white px-3 py-2 text-xs"
                    data-testid="design-bid-packages-empty"
                  >
                    <span className="text-ciq-gray-800">
                      No Bid Packages linked
                    </span>
                    {!disabled ? (
                      <DesignSuccessorsLinkAction
                        onClick={openLinkingDialog}
                        testId="design-bid-packages-link-button"
                      >
                        Link
                      </DesignSuccessorsLinkAction>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <FeatureLinkingDialog
        open={isModalOpen}
        headerComponent={
          <FeatureLinksDialogHeader
            title="Modify Bid Package Links"
            onCancel={() => {
              setIsModalOpen(false);
              resetFeaturesListStates();
              setSelectedFeatures(
                (linkedFeatures ?? []).map((feature: TLinkedBidPackage) =>
                  withDefaultMilestone(feature, milestones)
                )
              );
              setInEditMode(false);
            }}
            onSave={onSave}
            disableSaveButton={!inEditMode}
            disableCancelButton={isSavingLinks}
            saveButtonLoading={isSavingLinks}
          />
        }
        lhsComponent={
          <div className="h-full max-h-full overflow-hidden flex flex-col">
            {selectedFeatures.length > 0 ? (
              <div className="my-5 text-xs px-4">
                Linked Bid Packages ({selectedFeatures.length})
              </div>
            ) : null}
            <div className="min-w-0 grow overflow-x-hidden overflow-y-auto px-4">
              {linkedFeaturesDOMForDialog}
            </div>
          </div>
        }
        rhsComponent={
          <FeaturesGrid
            featuresList={featuresList}
            onCheckBoxAction={onCheckBoxAction}
            linkedFeatures={selectedFeatures}
            isLoadingFeaturesList={isLoadingFeaturesList}
          />
        }
      />
    </>
  );
}

export default DesignPackageSuccessorsColumn;
